import {
  ExtraProductInformation,
  ExtraProductInformationElement,
  ExtraProductInformationItem,
  ExtraProductInformationTagType
} from "../data-models/extra-product-information.model";

export interface ITagTypeApiResponse {
  slug: string;
  label: string;
  tags: string[];
}

export interface ITagApiResponse {
  slug: string;
  label: string;
}

export interface IElementApiResponse {
  element_id: number | string;
  tags: string[];
}

/** The part of the product oneshot this feature reads. */
export interface IOneShotWithExtraProductInformation {
  metadata?: {
    product?: {
      tags?: string[];
    };
    elements?: IElementApiResponse[];
    tagTypes?: ITagTypeApiResponse[];
    tags?: ITagApiResponse[];
  };
}

export class ExtraProductInformationFactory {

  /**
   * One row per tag type of the product, in the order of the product's tags: the tag type gives the name and the
   * tag gives the value. Several tags of one type share a row; a tag listed by two tag types belongs to the first.
   * A tag without a tag type or a label is left out. The elements of a sectional are read from their tags the same way.
   */
  public createFromOneShot(oneShot: IOneShotWithExtraProductInformation | null): ExtraProductInformation {
    const metadata = oneShot?.metadata;
    const tagTypeByTag = new Map<string, ITagTypeApiResponse>();
    this.asArray<ITagTypeApiResponse>(metadata?.tagTypes).forEach((tagType) =>
      this.asArray<string>(tagType?.tags).forEach((tagSlug) => {
        if (!tagTypeByTag.has(tagSlug)) tagTypeByTag.set(tagSlug, tagType);
      }));
    const labelByTag = new Map(this.asArray<ITagApiResponse>(metadata?.tags).map((tag) => [tag?.slug, this.asText(tag?.label)]));
    const createItems = (tagSlugs: unknown) => this.createItems(tagSlugs, tagTypeByTag, labelByTag);

    return new ExtraProductInformation(
      createItems(metadata?.product?.tags),
      this.createElements(metadata?.elements, createItems)
    );
  }

  private createItems(
    tagSlugs: unknown,
    tagTypeByTag: Map<string, ITagTypeApiResponse>,
    labelByTag: Map<string, string>
  ): ExtraProductInformationItem[] {
    const itemsByTagType = new Map<ITagTypeApiResponse, ExtraProductInformationItem>();

    this.asArray<string>(tagSlugs).forEach((tagSlug) => {
      const tagType = tagTypeByTag.get(tagSlug);
      const name = this.asText(tagType?.label);
      const value = labelByTag.get(tagSlug);
      if (!tagType || !name || !value) return;

      const item = itemsByTagType.get(tagType);
      itemsByTagType.set(
        tagType,
        new ExtraProductInformationItem(tagType.slug, name, item ? `${item.value}; ${value}` : value)
      );
    });

    return [...itemsByTagType.values()];
  }

  /** An element without an id cannot be matched with the configuration and is left out. */
  private createElements(
    apiElements: unknown,
    createItems: (tagSlugs: unknown) => ExtraProductInformationItem[]
  ): ExtraProductInformationElement[] {
    return this.asArray<IElementApiResponse>(apiElements)
      .filter((apiElement) => !!this.asText(apiElement?.element_id))
      .map((apiElement) => {
        const items = createItems(apiElement.tags);
        const valueOf = (tagType: ExtraProductInformationTagType) => items.find((item) => item.slug === tagType)?.value ?? "";

        return new ExtraProductInformationElement(
          this.asText(apiElement.element_id),
          valueOf(ExtraProductInformationTagType.ProductName),
          valueOf(ExtraProductInformationTagType.ShippingDimensions),
          valueOf(ExtraProductInformationTagType.ShippingWeight)
        );
      });
  }

  private asArray<T>(value: unknown): T[] {
    return Array.isArray(value) ? value : [];
  }

  private asText(value: unknown): string {
    if (value === null || value === undefined) return "";

    return String(value).trim();
  }
}
